import { getFunnelById, getLatestCampaignTest, testCampaign } from 'API/linkedin-funnel';
import { ErrorMessages } from 'enums';
import { CampaignTestMeta, CampaignTestingState } from 'features/co-pilot/campaign-builder/campaign-builder.types';
import { Funnel, FunnelContact } from 'features/co-pilot/co-pilot.types';
import _ from 'lodash';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Edge, Node } from 'reactflow';
import { getNodeIdByStepIndex } from 'utils/flowNode.utils';

interface Params {
  campaign: Funnel | null;
  campaignTesting: CampaignTestingState;
  setCampaignTesting: Dispatch<SetStateAction<CampaignTestingState>>;
  finalContacts: FunnelContact[];
  flowHasError: () => boolean;
  handleOpenFlowIncompleteDialog: () => void;
  handleOpenFlowEmptyDialog: () => void;
  nodes: Node[];
  edges: Edge[];
  setSelectedNode: (node: Node | null) => void;
  startCheckAccountStatusInterval: (accountId: string) => void;
  handleOpenUnableToGenerateMessageDialog: () => void;
  handleOpenErrorDialog: (error: any, endpoint: string, method: string) => void;
}

const useCampaignTestFlow = ({
  campaign,
  campaignTesting,
  setCampaignTesting,
  finalContacts,
  flowHasError,
  handleOpenFlowIncompleteDialog,
  handleOpenFlowEmptyDialog,
  nodes,
  edges,
  setSelectedNode,
  startCheckAccountStatusInterval,
  handleOpenUnableToGenerateMessageDialog,
  handleOpenErrorDialog,
}: Params) => {
  const campaignId = campaign?._id;
  // Erhöht sich, wenn ein hier gestarteter Test im Hintergrund weiterläuft — startet das Abholen neu.
  const [pollTick, setPollTick] = useState(0);

  // Letzten Test zeigen — auch einen, der per MCP lief. Läuft er noch, nachfragen bis fertig.
  useEffect(() => {
    if (_.isNil(campaignId)) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let polls = 0;

    const load = async () => {
      const res = await getLatestCampaignTest(campaignId);
      const latest = res?.response;
      if (cancelled || res?.error || _.isNil(latest)) return;
      const lastTest: CampaignTestMeta = {
        testId: latest.testId,
        source: latest.source,
        status: latest.status,
        startedAt: latest.startedAt,
        finishedAt: latest.finishedAt,
      };
      if (latest.status === 'running') {
        if (polls < 60) {
          polls += 1;
          setCampaignTesting((prev) => (prev.loading && !prev.lastTest ? prev : { ...prev, loading: true, lastTest }));
          timer = setTimeout(load, 5000);
        } else {
          setCampaignTesting((prev) => (prev.lastTest?.status === 'running' ? { ...prev, loading: false } : prev));
        }
        return;
      }
      setCampaignTesting((prev) => {
        // Schon angezeigt, oder gerade läuft ein hier gestarteter Test — dann nichts überschreiben.
        if (prev.lastTest?.testId === latest.testId && !_.isEmpty(prev.testContacts)) return prev;
        if (prev.loading && prev.lastTest?.status !== 'running') return prev;
        return {
          ...prev,
          loading: false,
          lastTest,
          testContacts: latest.testContacts || [],
          activeTestContactIndex: 0,
          activeMessageIndex: 0,
        };
      });
    };

    load();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [campaignId, pollTick]);

  const cleanupInvisibleTestContacts = (selectedTestContacts: string[]) => {
    const filteredContacts = finalContacts.filter((contact) => contact.preprocessedProfileAnalysis);
    const visibleTestContacts = _.take(filteredContacts, 5).map((contact) => contact.linkedInURL);

    const updatedSelectedTestContacts = selectedTestContacts.filter((contact) => visibleTestContacts.includes(contact));

    setCampaignTesting((prev) => ({
      ...prev,
      selectedTestContacts: updatedSelectedTestContacts,
    }));

    return updatedSelectedTestContacts;
  };

  const handleTestCampaign = async () => {
    if (_.isNil(campaign)) return;

    if (flowHasError()) {
      handleOpenFlowIncompleteDialog();
      return;
    }

    setCampaignTesting((prev) => ({ ...prev, loading: true, testContacts: [], activeTestContactIndex: 0, activeMessageIndex: 0 }));

    const resCampaign = await getFunnelById(campaign._id);

    if (!resCampaign.error && !_.isNil(resCampaign?.reactFlow)) {
      const { reactFlow } = resCampaign;

      if (_.isEmpty(reactFlow.nodes)) {
        handleOpenFlowEmptyDialog();
        setCampaignTesting((prev) => ({ ...prev, loading: false }));
        return;
      }
    }

    const { selectedTestContacts } = campaignTesting;
    const cleanedSelectedTestContacts = cleanupInvisibleTestContacts(selectedTestContacts);

    setSelectedNode(null);

    const res = await testCampaign(campaign._id, cleanedSelectedTestContacts);

    if (!res.error) {
      if (res.status === 'running' && res.testId) {
        // Mit gpt-6.1-sol dauert der Test länger als das Zeitlimit des Gateways — das
        // Ergebnis kommt aus der gemeinsamen Ablage, sobald es fertig ist.
        const startedAt = new Date().toISOString();
        setCampaignTesting((prev) => ({
          ...prev,
          loading: true,
          testContacts: [],
          lastTest: { testId: res.testId, source: 'app', status: 'running', startedAt, finishedAt: null },
        }));
        setPollTick((tick) => tick + 1);
        return;
      }
      const testContacts = res.response || [];
      const now = new Date().toISOString();
      setCampaignTesting((prev) => ({
        ...prev,
        loading: false,
        testContacts,
        lastTest: res.testId ? { testId: res.testId, source: 'app', status: 'done', startedAt: now, finishedAt: now } : null,
      }));

      const contact = testContacts[0];
      const messages = contact?.messages;
      const newMessageStepIndex = messages?.[0]?.stepIndex ?? undefined;
      const nodeId = getNodeIdByStepIndex(edges, nodes, newMessageStepIndex);

      if (!_.isNil(nodeId)) {
        const foundNode = nodes.find((node) => node.id === nodeId);
        if (!_.isNil(foundNode)) {
          setSelectedNode(foundNode);
        }
      }
    } else {
      if (res.error.message === ErrorMessages.ACCOUNT_OUT_OF_SYNC && campaign.linkedinAccount) {
        startCheckAccountStatusInterval(campaign.linkedinAccount);
      } else if (res.error.message?.includes(ErrorMessages.UNABLE_TO_GENERATE_MESSAGE)) {
        handleOpenUnableToGenerateMessageDialog();
      } else {
        handleOpenErrorDialog(res?.error, '/campaigns/:campaignId/test', 'post');
      }

      setCampaignTesting((prev) => ({ ...prev, loading: false }));
    }
  };

  return { handleTestCampaign };
};

export default useCampaignTestFlow;
